import type { HubEvent, HubHistoryEvent } from '@aiai/shared';
import { lineId, startedText, type FeedEntry } from './feed';

/** A one-shot animation on a planet of the universe (from a live /hub event or a replayed one). */
export interface HubEffect {
  id: string;
  /**
   * comet-out: sun -> planet (an agent started working); comet-home: planet -> sun (something shipped);
   * meteor: a streak onto the planet (new issue); crash: a ring + debris (gate failed / gave up);
   * flicker: the planet blinks (runtime error).
   */
  kind: 'comet-out' | 'comet-home' | 'meteor' | 'crash' | 'flicker';
  projectId: string;
  /** performance.now() timestamp the effect starts at (same clock as requestAnimationFrame). */
  at: number;
}

/** Effects older than this are finished and can be dropped. */
export const EFFECT_TTL_MS = 3000;

/**
 * Pure mappers from a real-time /hub event to a feed line / one-shot effects.
 * `codeFor` resolves an issue code from the page's snapshot by issueId (only
 * run.line and build run.started carry one on the wire).
 */
export function feedFromHubEvent(
  e: HubEvent,
  hrefFor: (e: HubEvent) => string | null,
  now: number,
  codeFor?: (issueId: string) => string | null | undefined,
): FeedEntry | null {
  const href = hrefFor(e);
  const code = e.issueCode ?? (e.issueId ? (codeFor?.(e.issueId) ?? null) : null);
  const tag = e.lane === 'build' ? ('E' as const) : ('PM' as const);
  switch (e.type) {
    case 'run.line':
      if (!e.text || !e.runId) return null;
      return { id: lineId(e.runId, e.text), at: now, tag, code, text: e.text, href };
    case 'run.started':
      if (!e.runId) return null;
      return { id: `start:${e.runId}`, at: now, tag, code, text: startedText(e.lane ?? 'build'), href };
    case 'pr.merged':
      return {
        id: `merged:${e.prUrl ?? e.at}`, at: now, tag: 'OK', code,
        text: e.prNumber ? `merged PR #${e.prNumber}` : 'merged a pull request', href,
      };
    case 'issue.arrived':
      return { id: `arrived:${e.issueId ?? e.at}`, at: now, tag: 'PM', code, text: 'new issue', href };
    case 'error.caught':
      return { id: `error:${e.issueId ?? e.runId ?? ''}:${e.at}`, at: now, tag: 'E', code, text: 'runtime error caught', href };
    default:
      return null;
  }
}

export function homeEffects(e: HubEvent, now: number): HubEffect[] {
  if (e.type === 'run.started' && e.runId) return [{ id: `out:${e.runId}`, kind: 'comet-out', projectId: e.projectId, at: now }];
  if (e.type === 'pr.merged') return [{ id: `home:live:${e.prUrl ?? e.at}`, kind: 'comet-home', projectId: e.projectId, at: now }];
  if (e.type === 'issue.arrived') return [{ id: `meteor:${e.issueId ?? e.at}`, kind: 'meteor', projectId: e.projectId, at: now }];
  if (e.type === 'error.caught') return [{ id: `flicker:${e.issueId ?? e.runId ?? e.at}`, kind: 'flicker', projectId: e.projectId, at: now }];
  if (e.type === 'run.line' && e.kind === 'gate' && e.text?.startsWith('Gate failed')) {
    return [{ id: `crash:${e.runId ?? ''}:${e.at}`, kind: 'crash', projectId: e.projectId, at: now }];
  }
  return [];
}

/**
 * A feed line for a replayed history event: same vocabulary as feedFromHubEvent,
 * but stamped with the event's real time, not "now".
 */
export function feedFromHistoryEvent(e: HubHistoryEvent, id: string, href: string | null): FeedEntry {
  const at = Date.parse(e.at);
  const base = { id, at: Number.isFinite(at) ? at : Date.now(), code: e.issueCode ?? null, href };
  const tag = e.lane === 'build' ? ('E' as const) : ('PM' as const);
  switch (e.type) {
    case 'run.started':
      return { ...base, tag, text: startedText(e.lane ?? 'build') };
    case 'pr.merged':
      return { ...base, tag: 'OK', text: e.prNumber ? `merged PR #${e.prNumber}` : 'merged a pull request' };
    case 'issue.arrived':
      return { ...base, tag: 'PM', text: 'new issue' };
    case 'error.caught':
      return { ...base, tag: 'E', text: 'runtime error caught' };
    case 'gate.failed':
      return { ...base, tag: 'E', text: 'build failed' };
    case 'gave.up':
      return { ...base, tag: 'E', text: 'gave up' };
  }
}
